/**
 * Label colour for the TeamThirdsPitch bands (PMA-COCK-235). The bands are the
 * team colour at alpha 0.15..0.75 over the pitch green, so a light kit gives a
 * light band and white labels vanish. Pick white or black, whichever reaches
 * the higher WCAG contrast against the band's composited colour. Slate-900 was
 * rejected as the dark option: against the worst mid-luminance band it tops
 * out near 4.2:1, black never drops below 4.58:1.
 */
export const PITCH_GREEN: [number, number, number] = [0x2d, 0x6a, 0x2e];

export function bandAlpha(sharePct: number): number {
  return 0.15 + 0.6 * (sharePct / 100);
}

function parseHex(hex: string): [number, number, number] {
  const m = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (!m) return [100, 116, 139];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function luminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrastRatio(
  a: [number, number, number],
  b: [number, number, number],
): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** Team colour tinted at `alpha` over the pitch green. */
export function effectiveBandColor(
  hex: string,
  alpha: number,
): [number, number, number] {
  const c = parseHex(hex);
  return [0, 1, 2].map((i) => c[i] * alpha + PITCH_GREEN[i] * (1 - alpha)) as [
    number,
    number,
    number,
  ];
}

export type BandLabelTone = "light" | "dark";

export function bandLabel(
  hex: string,
  sharePct: number,
): { tone: BandLabelTone; ratio: number } {
  const bg = effectiveBandColor(hex, bandAlpha(sharePct));
  const light = contrastRatio([255, 255, 255], bg);
  const dark = contrastRatio([0, 0, 0], bg);
  return light >= dark
    ? { tone: "light", ratio: light }
    : { tone: "dark", ratio: dark };
}
